import {
  CircadianPhase,
  DefaultEventCategoryId,
  EMASatisfactionType,
  EnergyFeedbackType,
  EventCategorySet,
} from '@demind-inc/core';

export type DataTFunction = (
  key: string,
  options?: Record<string, unknown> & { defaultValue?: string }
) => string;

export const getSatisfactionText = (
  t: DataTFunction,
  type: EMASatisfactionType | 'unanswered'
) => t(`data.satisfaction.levels.${type}`);

export const getEnergyFeedbackLabel = (t: DataTFunction, type: EnergyFeedbackType) =>
  t(`data.energyFeedback.${type}`);

export const getZoneLevelLabel = (t: DataTFunction, level: string) =>
  t(`data.task.zoneLevels.${level}`, { defaultValue: level });

export const getCircadianPhaseTitle = (
  t: DataTFunction,
  phase: CircadianPhase | string,
  fallback = ''
) => t(`data.circadian.phases.${phase}`, { defaultValue: fallback || phase });

export const getDefaultEventCategoryTitle = (
  t: DataTFunction,
  categoryId: DefaultEventCategoryId | string,
  fallback = ''
) => t(`data.eventCategories.${categoryId}.title`, { defaultValue: fallback || categoryId });

export const getDefaultEventCategoryDescription = (
  t: DataTFunction,
  categoryId: DefaultEventCategoryId | string,
  fallback = ''
) =>
  t(`data.eventCategories.${categoryId}.description`, {
    defaultValue: fallback,
  });

export const getFluctuationReasonLabel = (t: DataTFunction, reason?: string) =>
  t(`data.circadian.fluctuationReasons.${reason || 'event'}`, {
    defaultValue: reason || '',
  });

export const getEventCategoryEnergyTooltipTitle = (
  t: DataTFunction,
  category?: EventCategorySet
) => {
  const title = category
    ? getDefaultEventCategoryTitle(t, category.id as DefaultEventCategoryId, category.name)
    : t('data.events.event');

  return t('data.circadian.tooltips.eventAdjustmentTitle', { title });
};

export const formatDurationHoursMinutes = (
  t: DataTFunction,
  hours?: number | null,
  minutes?: number | null
) => {
  if (hours === undefined || hours === null || minutes === undefined || minutes === null) {
    return t('data.common.emptyDuration');
  }

  return t('data.common.durationHoursMinutes', { hours, minutes });
};

export const formatMinutes = (t: DataTFunction, minutes: number) =>
  t('data.common.minutesValue', { minutes: Math.abs(minutes) });

export const getWeekdayLabels = (
  t: DataTFunction,
  variant: 'narrow' | 'short' = 'short'
) => [
  t(`data.weekdays.${variant}.sunday`),
  t(`data.weekdays.${variant}.monday`),
  t(`data.weekdays.${variant}.tuesday`),
  t(`data.weekdays.${variant}.wednesday`),
  t(`data.weekdays.${variant}.thursday`),
  t(`data.weekdays.${variant}.friday`),
  t(`data.weekdays.${variant}.saturday`),
];

export const formatDataDate = (
  language: string,
  date: Date,
  options: Intl.DateTimeFormatOptions
) => new Intl.DateTimeFormat(language, options).format(date);
